import type { Subject } from '@carii/casl-abilities';
import { useQuery } from '@tanstack/react-query';
import { Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { ListSearch } from '../../components/ui/ListSearch.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { RowActions } from '../../components/ui/RowActions.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useConfirmedMutation } from '../../hooks/useConfirmedMutation.js';
import { useListControls } from '../../hooks/useListControls.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS, type ResourceName } from '../../services/resourceKeys.js';

/** One screenful. Matches the other paginated admin lists rather than inventing a third size. */
const PAGE_SIZE = 20;

/** What `config.apiPath` answers once a page has been asked for (162). */
interface PagedTaxonomy {
  items: TaxonomyRow[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface TaxonomyRow {
  _id: string;
  name: string;
  status: string;
  [key: string]: unknown;
}

export interface TaxonomyListConfig {
  title: string;
  description: string;
  apiPath: string; // e.g. '/car-brands'
  basePath: string; // e.g. '/car-brands' (admin routes)
  resource: ResourceName; // 161: names the cache entries this screen reads and invalidates
  subject: Subject; // CASL subject for delete gating
  entityLabel: string; // e.g. 'brand'
  extraColumns?: Column<TaxonomyRow>[]; // inserted before Status
}

/**
 * Shared admin list for a global taxonomy entity (brand/category/model/feature).
 * Columns are Name → [extra] → Status → Actions; delete is CASL-gated on the
 * entity subject (admins only — suppliers/hosts have read-only taxonomy so no
 * mutating actions render for them).
 */
export function TaxonomyListPage({ config }: { config: TaxonomyListConfig }) {
  const ability = useAbility();
  const canManage = ability.can('manage', config.subject);

  // 162 (#21): a pager and a search box. One change, four screens — car brands, models,
  // categories and features are all wrappers over this component.
  const { page, setPage, searchInput, setSearchInput, search } = useListControls();

  const listPath = (() => {
    const params = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
    // Only when there is something to filter by: a cleared box must show the whole list, not an
    // empty one (FR-004).
    if (search) params.set('q', search);
    return `${config.apiPath}?${params.toString()}`;
  })();

  const { data, isLoading, error } = useQuery({
    queryKey: [...RESOURCE_KEYS[config.resource].list, page, search],
    // 🔴 Asking for a page changes the response shape — this endpoint answers a BARE ARRAY to a
    // caller that asks for none, because the customer app's search filter panel reads it that way.
    // See `backend/src/shared/listResponse.ts`.
    queryFn: () => api.get<PagedTaxonomy>(listPath),
    // 063 (FR-010): a refusal is not a transient fault; retrying it only keeps
    // the caller reading the empty state.
    retry: false,
  });

  const remove = useConfirmedMutation({
    mutationFn: (id: string) => api.delete(`${config.apiPath}/${id}`),
    invalidateKey: RESOURCE_KEYS[config.resource].list,
  });

  const columns: Column<TaxonomyRow>[] = [
    { key: 'name', header: 'Name' },
    ...(config.extraColumns ?? []),
    {
      key: 'status',
      header: 'Status',
      render: (r) => <StatusBadge status={r.status} />,
    },
    {
      key: '_id',
      header: 'Actions',
      render: (r) => (
        <RowActions
          actions={[
            canManage && {
              key: 'edit',
              label: 'Edit',
              href: `${config.basePath}/${r._id}/edit`,
              icon: <Pencil className="h-4 w-4" />,
            },
            canManage && {
              key: 'delete',
              label: 'Delete',
              variant: 'danger',
              confirmMessage: `Delete ${config.entityLabel} "${r.name}"?`,
              onClick: () => remove.run(r._id),
              icon: <Trash2 className="h-4 w-4" />,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={config.title}
        description={config.description}
        actions={
          canManage ? (
            <Link to={`${config.basePath}/new`}>
              <Button>+ New</Button>
            </Link>
          ) : undefined
        }
      />
      <ListSearch
        value={searchInput}
        onChange={setSearchInput}
        label={`Search ${config.title.toLowerCase()} by name`}
        testId="taxonomy-search"
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(r) => r._id}
        emptyMessage={
          // A search that matched nothing is a different fact from an empty catalogue, and an
          // operator who reads the wrong one goes looking in the wrong place (158 FR-009).
          search
            ? `No ${config.entityLabel} entries match “${search}”.`
            : `No ${config.entityLabel} entries yet`
        }
        // 063: one branch here covers Brands, Categories and Features, which are
        // wrappers over this component.
        error={error as { status?: number } | null}
        pagination={{
          page: page,
          totalPages: data?.pages ?? 1,
          onPageChange: setPage,
        }}
      />
    </div>
  );
}
